import { Input } from '@chiroup/components';
import { useForm } from '@chiroup/hooks';
import { EyeIcon } from '@heroicons/react/24/solid';
import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import userService from '../../services/user.service';
import Button from '../common/Button';
import { PasswordValidator } from '../common/validators/passwordValidator';
import LoggedOutFooter from '../layout/LoggedOutFooter';
import LoginContainer from './LoginContainer';
import PasswordValidationMeter from './PasswordValidationMeter';
import { confirmResetPassword, signIn } from '@aws-amplify/auth';

export enum ForgotPasswordStep {
  sendCode,
  setPassword,
  welcomeSent,
}

type Props = {
  initialEmail?: string;
  initialStep?: ForgotPasswordStep;
};

const ForgotPassword: React.FC<Props> = ({
  initialEmail = '',
  initialStep = ForgotPasswordStep.sendCode,
}) => {
  const passwordRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { value, registerSubmit, onChange, errors, isSubmitting } = useForm(
    { email: initialEmail, code: '', password: '' },
    {
      email: {
        function: {
          value: (val) => {
            if (step === ForgotPasswordStep.sendCode && !val.email) {
              return 'Email is required.';
            }
            return false;
          },
        },
      },
      code: {
        function: {
          value: (val) => {
            if (step === ForgotPasswordStep.setPassword && !val.code) {
              return 'Enter the verification code you received in your email.';
            }
            return false;
          },
        },
      },
      password: {
        function: {
          value: (val) => {
            if (step === ForgotPasswordStep.setPassword && !val.password) {
              return 'New password is required.';
            }
            return false;
          },
        },
        pattern: PasswordValidator,
      },
    },
  );
  const [step, setStep] = useState<ForgotPasswordStep>(initialStep);
  const [isPasswordVisible, setPasswordVisibility] = useState(false);

  const togglePasswordVisibility = () => {
    setPasswordVisibility(!isPasswordVisible);
  };

  const onSubmit = async () => {
    if (!value.email) {
      return;
    }
    if (step === ForgotPasswordStep.sendCode) {
      try {
        // const res = await Auth.forgotPassword(value.email);
        const res = await userService.forgotPassword(value.email);
        setStep(ForgotPasswordStep.setPassword);
        return res;
      } catch (err) {
        console.error({ err });
      }
    } else if (step === ForgotPasswordStep.setPassword) {
      if (!value.code || !value.password) {
        return;
      }
      try {
        const res = await confirmResetPassword({
          username: value.email,
          confirmationCode: value.code,
          newPassword: value.password,
        });
        await signIn({ username: value.email, password: value.password });
        navigate('/');
        return res;
      } catch (err) {
        console.error({ err });
      }
    }
  };

  return (
    <LoginContainer>
      <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
        Reset your password
      </h2>
      <p className="mt-1 text-sm text-gray-600">
        Remember?{' '}
        <Link
          to="/"
          className="font-medium text-primary-500 hover:text-primary-300"
        >
          Sign in
        </Link>
      </p>
      <form
        className="mt-8 space-y-6"
        onSubmit={registerSubmit(onSubmit, {})}
        autoComplete="off"
      >
        {step === ForgotPasswordStep.sendCode ? (
          <Input
            label="Email address"
            name="email"
            onChange={onChange('email')}
            value={value.email}
            errors={errors.fieldErrors?.email}
          />
        ) : (
          <div>
            <Input
              label="Verification code"
              name="code"
              onChange={onChange('code')}
              value={value.code}
              errors={errors.fieldErrors?.code}
              autoComplete="off"
            />
            <Input
              label="New password"
              name="password"
              onChange={onChange('password')}
              type={isPasswordVisible ? 'text' : 'password'}
              value={value.password}
              errors={errors.fieldErrors?.password}
              autoComplete="new-password"
              ref={passwordRef}
              icon={
                <EyeIcon
                  className="w-5 h-5 text-gray-300 cursor-pointer hover:text-gray-400"
                  onClick={togglePasswordVisibility}
                />
              }
              clickIcon
            />
            <PasswordValidationMeter
              password={value.password}
              errors={errors.fieldErrors?.password}
            />
          </div>
        )}
        <Button
          type="submit"
          text={
            step === ForgotPasswordStep.sendCode
              ? 'Send code'
              : 'Change password'
          }
          loading={isSubmitting}
          fullWidth
        />
      </form>
      <div className="mt-12">
        <LoggedOutFooter />
      </div>
    </LoginContainer>
  );
};

export default ForgotPassword;
